import { useState } from "react";
import React from "react";
import Icon from "../components/Icon";
import { LEAD_STAGES, STAGE_COLORS } from "../modules/Pipeline";
import { fmt, getCrewColor } from "../utils";

function Dashboard({ t, customers, leads, estimates, invoices, jobs, comms, timesheets, addComm, setPage, lang, teamMembers, settings, sendThankYouEmail, onNavigateToEstimate }) {
  const [expandedMsg, setExpandedMsg] = useState(null);
  const [tyState, setTyState] = useState({});
  const [replyTexts, setReplyTexts] = useState({});
  const [sendingReply, setSendingReply] = useState(false);
  const [suiviExpanded, setSuiviExpanded] = useState(false);
  const [suiviSort, setSuiviSort] = useState({col:"enRetard",dir:"desc"});

  const LS_KEY = "taf-dismissed-feed-items";
  const SS_KEY = "taf-dismissed-feed-items-session";
  const [dismissedKeys, setDismissedKeys] = useState(()=>{
    try {
      const ls = JSON.parse(localStorage.getItem(LS_KEY)||"[]");
      const ss = JSON.parse(sessionStorage.getItem(SS_KEY)||"[]");
      return new Set([...ls,...ss]);
    } catch { return new Set(); }
  });

  const dismissItem = (key, storage) => {
    setDismissedKeys(prev=>{
      const next = new Set(prev);
      next.add(key);
      if (storage==="local") {
        try { const arr=JSON.parse(localStorage.getItem(LS_KEY)||"[]"); localStorage.setItem(LS_KEY,JSON.stringify([...new Set([...arr,key])])); } catch {}
      } else {
        try { const arr=JSON.parse(sessionStorage.getItem(SS_KEY)||"[]"); sessionStorage.setItem(SS_KEY,JSON.stringify([...new Set([...arr,key])])); } catch {}
      }
      return next;
    });
  };

  const clearAllDismissed = () => {
    try { localStorage.removeItem(LS_KEY); sessionStorage.removeItem(SS_KEY); } catch {}
    setDismissedKeys(new Set());
  };

  const activeLeads = leads.filter(l=>!["won","lost"].includes(l.stage));
  const wonLeads = leads.filter(l=>l.stage==="won");
  const pipelineVal = activeLeads.reduce((a,l)=>a+(l.value||0),0);
  const activeJobs = jobs.filter(j=>j.status==="inProgress");
  const stageData = LEAD_STAGES.slice(0,7).map(s=>({stage:s,count:leads.filter(l=>l.stage===s).length}));
  const maxCount = Math.max(...stageData.map(s=>s.count),1);
  const recent = [...customers.slice(-3).map(c=>({type:"customer",text:`${t.dashboard.recentActivity}: ${c.firstName} ${c.lastName}`,time:""})), ...estimates.slice(-3).map(e=>({type:"estimate",text:`Soumission: ${e.title||("#"+(e.number||""))}`,time:""}))].slice(0,7);

  // ── Feed helpers ──
  const now = new Date();
  const toDate = (v) => { if (!v) return null; if (v?.toDate) return v.toDate(); return new Date(v); };
  const daysSince = (v) => { const d = toDate(v); if (!d) return Infinity; return Math.floor((now - d) / 86400000); };
  const timeAgo = (v) => {
    const d = toDate(v); if (!d) return "";
    const diffMins = Math.floor((now - d) / 60000);
    const diffHours = Math.floor((now - d) / 3600000);
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffMins < 5) return t.dashboard.justNow;
    if (diffHours < 24) return t.dashboard.hoursAgo.replace("{n}", diffHours);
    return t.dashboard.daysAgo.replace("{n}", diffDays);
  };
  const cName = (cid) => { const c = (customers||[]).find(x=>x.id===cid); return c ? `${c.firstName} ${c.lastName}` : "—"; };

  // ── Feed item computation ──
  const inboundMsgs = (comms||[]).filter(c=>c.type==="portal"&&c.direction==="inbound"&&!c.isTask);
  const unansweredMsgs = inboundMsgs.filter(msg => {
    const msgTime = toDate(msg.createdAt);
    return !msgTime || !(comms||[]).some(r=>r.type==="portal"&&r.direction==="outbound"&&r.customerId===msg.customerId&&toDate(r.createdAt)>msgTime);
  });
  const awaitingEstimates = (estimates||[]).filter(e=>e.status==="sent"&&daysSince(e.createdAt)>3);
  const approvalComms = (comms||[]).filter(c=>c.portalApprovedAt&&daysSince(c.portalApprovedAt)<=7);
  const approvedEstimateIds = new Set(approvalComms.map(c=>c.estimateId).filter(Boolean));
  const approvedEstimates = (estimates||[]).filter(e=>e.status==="approved"&&approvedEstimateIds.has(e.id));
  const overdueInvoices = (invoices||[]).filter(inv=>inv.status!=="Paid"&&daysSince(inv.createdAt)>30);
  const pendingTimesheets = (timesheets||[]).filter(ts=>ts.status==="submitted");
  const rejectedTimesheets = (timesheets||[]).filter(ts=>ts.status==="rejected");
  const todayDash = now.toISOString().split("T")[0];
  const overdueMilestones = [];
  (invoices||[]).forEach(inv=>{
    (inv.paymentSchedule||[]).forEach(ms=>{
      if (ms.status==="pending" && ms.dueDate && ms.dueDate < todayDash) {
        overdueMilestones.push({inv, ms});
      }
    });
  });
  const pendingCOs = [];
  (invoices||[]).forEach(inv=>{
    (inv.changeOrders||[]).forEach(co=>{
      if (co.status==="pending" && co.portalSentAt && daysSince(co.portalSentAt) > 3) {
        pendingCOs.push({inv, co});
      }
    });
  });
  const upcomingDeposits = [];
  const in21 = new Date(now); in21.setDate(in21.getDate()+21);
  const in21Str = in21.toISOString().split("T")[0];
  (invoices||[]).forEach(inv=>{
    (inv.paymentSchedule||[]).forEach(ms=>{
      if (ms.status==="pending" && !ms.sentAt && ms.dueDate && ms.dueDate >= todayDash && ms.dueDate <= in21Str) {
        upcomingDeposits.push({inv, ms});
      }
    });
  });

  // ── New feed types ──
  const draftEstimates = (estimates||[]).filter(e=>e.status==="draft"&&daysSince(e.createdAt)>7);

  const invoicedEstimateIdsSet = new Set((invoices||[]).map(inv=>inv.estimateId).filter(Boolean));
  const invoicedJobIdsSet = new Set((invoices||[]).map(inv=>inv.jobId).filter(Boolean));
  const completedNoInvoice = (jobs||[]).filter(j=>{
    if (j.status!=="completed") return false;
    if (!j.completedAt) return false;
    if (daysSince(j.completedAt)<=3) return false;
    if (daysSince(j.completedAt)>30) return false;
    return !(invoicedJobIdsSet.has(j.id)||(j.estimateId&&invoicedEstimateIdsSet.has(j.estimateId)));
  });

  const paidInvoiceFollowUp = (invoices||[]).filter(inv=>{
    if (inv.status!=="Paid") return false;
    if (inv.paidAt&&daysSince(inv.paidAt)>30) return false;
    return !(comms||[]).some(c=>c.invoiceId===inv.id&&c.type==="thank-you");
  });

  // ── Today ──
  const activeJobsToday = (jobs||[]).filter(j =>
    j.startDate && j.endDate &&
    j.startDate <= todayDash && j.endDate >= todayDash &&
    (j.status === 'inProgress' || j.status === 'pending')
  );
  const onSiteTimesheets = (timesheets||[]).filter(ts =>
    ts.punchIn &&
    ts.punchIn.slice(0,10) === todayDash &&
    !ts.punchOut &&
    ts.status !== 'rejected'
  );
  const paymentsToday = [];
  (invoices||[]).forEach(inv => {
    (inv.paymentSchedule||[]).forEach(ms => {
      if (ms.dueDate === todayDash && ms.status !== 'paid') paymentsToday.push({ inv, ms });
    });
  });
  const todayEmpty = !activeJobsToday.length && !onSiteTimesheets.length && !paymentsToday.length;
  const todayDateStr = (() => {
    const raw = now.toLocaleDateString(lang==='fr'?'fr-CA':'en-CA', {weekday:'long',day:'numeric',month:'long'});
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  })();
  const fmtTime = iso => { if (!iso) return ""; const d = new Date(iso); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; };
  const jobTitleById = id => (jobs||[]).find(j=>j.id===id)?.title||"—";

  // ── Financial summary ──
  const invoicedEstimateIds = new Set((invoices||[]).map(inv=>inv.estimateId).filter(Boolean));
  const aEnvoyerEsts = (estimates||[]).filter(e=>e.status==="approved"&&!invoicedEstimateIds.has(e.id));
  const aEnvoyerCount = aEnvoyerEsts.length;
  const aEnvoyerTotal = aEnvoyerEsts.reduce((a,e)=>{const sub=(e.sections||[]).reduce((b,s)=>(s.items||[]).reduce((c,i)=>c+(i.unitPrice||0)*(i.qty||1),b),0);return a+sub;},0);

  const enAttenteInvIds = new Set();
  let enAttenteTotal = 0;
  (invoices||[]).forEach(inv=>{
    (inv.paymentSchedule||[]).forEach(ms=>{
      if ((ms.status==="pending"||ms.status==="sent")&&!(ms.dueDate&&ms.dueDate<todayDash)) {
        enAttenteInvIds.add(inv.id); enAttenteTotal+=ms.amount||0;
      }
    });
  });

  const enRetardTotal = overdueMilestones.reduce((a,{ms})=>a+(ms.amount||0),0);
  const enRetardInvIds = new Set(overdueMilestones.map(({inv})=>inv.id));

  const currentYear = now.getFullYear().toString();
  let encaisseTotal = 0;
  (invoices||[]).forEach(inv=>{
    (inv.paymentSchedule||[]).forEach(ms=>{
      if (ms.status==="paid"&&ms.paidAt&&ms.paidAt.startsWith(currentYear)) encaisseTotal+=parseFloat(ms.paidAmount)||ms.amount||0;
    });
  });

  // ── Per-job follow-up rows ──
  const suiviRowsRaw = (jobs||[])
    .filter(j=>j.status!=="cancelled")
    .map(j=>{
      const cust = (customers||[]).find(x=>x.id===j.customerId);
      const clientName = cust?`${cust.firstName} ${cust.lastName}`:"—";
      const custEsts = (estimates||[]).filter(e=>e.customerId===j.customerId).sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||""));
      const est = custEsts[0]||null;
      const custInvs = (invoices||[]).filter(inv=>inv.customerId===j.customerId||(est&&inv.estimateId===est.id)).sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||""));
      const inv = custInvs[0]||null;

      let estCell;
      if (!est) estCell = <span style={{color:"var(--text-3)"}}>—</span>;
      else if (est.status==="draft") estCell = <span style={{color:"var(--text-3)"}}>Brouillon</span>;
      else if (est.status==="sent") { const d=daysSince(est.createdAt); estCell = <span style={{color:"#d97706",fontWeight:600}}>Envoyée {d}j</span>; }
      else if (est.status==="approved") estCell = invoicedEstimateIds.has(est.id)?<span style={{color:"#2563eb",fontWeight:600}}>Facturée</span>:<span style={{color:"#16a34a",fontWeight:600}}>Approuvée</span>;
      else estCell = <span style={{color:"var(--text-3)"}}>{est.status}</span>;

      let invCell; let overdueAmt = 0;
      if (!inv) invCell = <span style={{color:"var(--text-3)"}}>—</span>;
      else {
        const overdueMs = (inv.paymentSchedule||[]).filter(ms=>ms.status==="pending"&&ms.dueDate&&ms.dueDate<todayDash);
        const pendingMs = (inv.paymentSchedule||[]).filter(ms=>ms.status==="pending"||ms.status==="sent");
        const paidMs = (inv.paymentSchedule||[]).filter(ms=>ms.status==="paid");
        overdueAmt = overdueMs.reduce((a,ms)=>a+(ms.amount||0),0);
        const allPaid = (inv.paymentSchedule||[]).length>0&&pendingMs.length===0;
        if (inv.status==="Paid"||allPaid) invCell = <span style={{color:"#16a34a",fontWeight:600}}>Payée ✓</span>;
        else if (overdueAmt>0) { const days=Math.floor((now-new Date(overdueMs[0].dueDate))/86400000); invCell = <span style={{color:"var(--red)",fontWeight:600}}>En retard {days}j</span>; }
        else if (paidMs.length>0&&pendingMs.length>0) { const partAmt=paidMs.reduce((a,ms)=>a+(ms.paidAmount||ms.amount||0),0); invCell = <span style={{color:"#2563eb",fontWeight:600}}>Partielle {fmt(partAmt)}</span>; }
        else invCell = <span style={{color:"#d97706",fontWeight:600}}>En attente</span>;
      }

      const lastSent = cust?.portalSentAt
        ? `Portal: ${new Date(cust.portalSentAt).toLocaleDateString("fr-CA",{month:"short",day:"numeric"})}`
        : "—";

      let actionBtn=null,actionPage="invoices",actionColor=null;
      if (est&&est.status==="approved"&&!invoicedEstimateIds.has(est.id)) { actionBtn=t.dashboard.facturer; actionColor="#d97706"; }
      else if (inv&&overdueAmt>0) { actionBtn=t.dashboard.relancer; actionColor="var(--red)"; }
      else if (est&&est.status==="sent"&&daysSince(est.createdAt)>5) { actionBtn=t.dashboard.envoyer; actionPage="estimates"; actionColor="#d97706"; }

      return {job:j,clientName,est,inv,estCell,invCell,overdueAmt,lastSent,actionBtn,actionPage,actionColor};
    })
    .filter(row=>row.actionBtn||row.overdueAmt>0||(row.inv&&(row.inv.paymentSchedule||[]).some(ms=>ms.status==="pending"||ms.status==="sent")));

  const suiviRows = [...suiviRowsRaw].sort((a,b)=>{
    const dir=suiviSort.dir==="asc"?1:-1;
    if (suiviSort.col==="enRetard") return (b.overdueAmt-a.overdueAmt)*dir;
    if (suiviSort.col==="projet") return a.job.title.localeCompare(b.job.title)*dir;
    if (suiviSort.col==="client") return a.clientName.localeCompare(b.clientName)*dir;
    return 0;
  });

  const allFeedItems = [
    ...unansweredMsgs.map(m=>({ type:"message", key:`msg-${m.id}`, storage:"local", emoji:"💬", title:t.dashboard.portalMessage, bold:cName(m.customerId), sub:(m.body||"").slice(0,60)+((m.body||"").length>60?"…":""), timeVal:m.createdAt, data:m, urgency:0 })),
    ...(rejectedTimesheets.length>0?[{ type:"timesheetsRejected", key:"ts-rejected", storage:"session", emoji:"🔴", title:`${rejectedTimesheets.length} ${t.dashboard.timesheetsRejected}`, bold:"", sub:"", timeVal:null, urgency:1 }]:[]),
    ...overdueMilestones.map(({inv,ms})=>({ type:"milestone", key:`ms-${inv.id}-${ms.id}`, storage:"session", emoji:"💰", title:t.dashboard.paymentOverdue, bold:`${cName(inv.customerId)} — ${ms.label}`, sub:`${Math.floor((now-new Date(ms.dueDate))/86400000)} ${t.dashboard.daysOverdue}`, timeVal:ms.dueDate, data:inv, urgency:2 })),
    ...pendingCOs.map(({inv,co})=>({ type:"pendingCO", key:`co-${inv.id}-${co.id}`, storage:"session", emoji:"🔄", title:t.dashboard.pendingCO, bold:`${cName(inv.customerId)} — ${co.number}`, sub:`${daysSince(co.portalSentAt)} ${t.dashboard.daysWaiting}`, timeVal:co.portalSentAt, data:inv, urgency:2 })),
    ...overdueInvoices.map(inv=>({ type:"invoice", key:`inv-${inv.id}`, storage:"session", emoji:"⚠️", title:t.dashboard.invoiceOverdue, bold:`${inv.number?"#"+inv.number+" — ":""}${cName(inv.customerId)}`, sub:`${daysSince(inv.createdAt)} ${t.dashboard.daysOverdue}`, timeVal:inv.createdAt, data:inv, urgency:2 })),
    ...paidInvoiceFollowUp.map(inv=>({ type:"thankYou", key:`thankyou-${inv.id}`, storage:"local", emoji:"🙏", title:lang==="fr"?"Facture payée — envoyer un suivi":"Invoice paid — send follow-up", bold:`${inv.number?"#"+inv.number+" — ":""}${cName(inv.customerId)}`, sub:"", timeVal:inv.paidAt||null, data:inv, urgency:2 })),
    ...upcomingDeposits.map(({inv,ms})=>({ type:"upcomingDeposit", key:`dep-${inv.id}-${ms.id}`, storage:"session", emoji:"📅", title:t.dashboard.upcomingDeposit, bold:`${cName(inv.customerId)} — ${ms.label}`, sub:`${fmt(ms.amount)} ${lang==="fr"?"dû le":"due"} ${ms.dueDate}`, timeVal:ms.dueDate, data:inv, urgency:3 })),
    ...(pendingTimesheets.length>0?[{ type:"timesheetsPending", key:"ts-pending", storage:"session", emoji:"📋", title:`${pendingTimesheets.length} ${t.dashboard.timesheetsPending}`, bold:"", sub:"", timeVal:null, urgency:3 }]:[]),
    ...completedNoInvoice.map(j=>{const days=daysSince(j.completedAt);return { type:"completedNoInvoice", key:`no-invoice-${j.id}`, storage:"session", emoji:"🧾", title:lang==="fr"?`Travail complété sans facture depuis ${days}j`:`Completed job has no invoice for ${days} days`, bold:`${j.title} — ${cName(j.customerId)}`, sub:"", timeVal:j.completedAt, data:j, urgency:3 };}),
    ...awaitingEstimates.map(e=>({ type:"estimate", key:`est-${e.id}`, storage:"local", emoji:"📄", title:t.dashboard.estimateAwaiting, bold:`${e.title||("Soumission #"+(e.number||""))} — ${cName(e.customerId)}`, sub:`${daysSince(e.createdAt)} ${t.dashboard.daysSinceSent}`, timeVal:e.createdAt, data:e, urgency:4 })),
    ...draftEstimates.map(e=>{const days=daysSince(e.createdAt);return { type:"draftEst", key:`draft-est-${e.id}`, storage:"session", emoji:"✏️", title:lang==="fr"?`Soumission en brouillon depuis ${days}j`:`Draft estimate unsent for ${days} days`, bold:`${e.title||("Soumission #"+(e.number||""))} — ${cName(e.customerId)}`, sub:"", timeVal:e.createdAt, data:e, urgency:4 };}),
    ...approvedEstimates.map(e=>{ const ac=approvalComms.find(c=>c.estimateId===e.id); return { type:"approved", key:`app-${e.id}`, storage:"local", emoji:"✅", title:t.dashboard.estimateApproved, bold:`${e.title||("Soumission #"+(e.number||""))} — ${cName(e.customerId)}`, sub:ac?timeAgo(ac.portalApprovedAt):"", timeVal:ac?.portalApprovedAt, data:e, urgency:5 }; }),
  ];
  const feedItems = allFeedItems.filter(item=>!dismissedKeys.has(item.key));
  const hasDismissed = dismissedKeys.size > 0 || allFeedItems.length > 3;

  const sendReply = async (msg) => {
    const body = (replyTexts[msg.id]||"").trim();
    if (!body || sendingReply) return;
    setSendingReply(true);
    await addComm({ type:"portal", direction:"outbound", subject:"Réponse", body, customerId:msg.customerId, date:new Date().toISOString().split("T")[0], isTask:false, taskDone:false });
    setReplyTexts(p=>({...p,[msg.id]:""}));
    setExpandedMsg(null);
    setSendingReply(false);
  };

  return (
    <div>
      <div className="page-header"><div className="page-header-row"><div><h1>{t.dashboard.title}</h1><p>{t.dashboard.subtitle}</p></div></div></div>
      <div className="page-body">

        {/* ── Today ── */}
        <div style={{marginBottom:24}}>
          <div style={{display:"flex",alignItems:"baseline",gap:10,marginBottom:12}}>
            <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:15,color:"var(--text)"}}>{lang==='fr'?'Aujourd\'hui':'Today'}</div>
            <div style={{fontSize:12,color:"var(--text-3)",fontWeight:500}}>{todayDateStr}</div>
          </div>
          {todayEmpty ? (
            <div style={{padding:"12px 16px",background:"var(--surface)",border:"1px solid var(--border)",borderRadius:10,color:"var(--text-3)",fontSize:13}}>
              {lang==='fr'?'Aucune activité aujourd\'hui':'No activity today'}
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>

              {/* Active jobs */}
              {activeJobsToday.length>0&&(
                <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:10,overflow:"hidden"}}>
                  <div style={{padding:"8px 14px",borderBottom:"1px solid var(--border)",fontSize:11,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".07em",background:"var(--bg-2)"}}>
                    {lang==='fr'?'Chantiers actifs':'Active Jobs'} <span style={{fontWeight:400,opacity:.6}}>({activeJobsToday.length})</span>
                  </div>
                  {activeJobsToday.map((j,i)=>{
                    const cust=(customers||[]).find(c=>c.id===j.customerId);
                    const custName=cust?`${cust.firstName} ${cust.lastName}`:"—";
                    const statusLabel=t.jobs?.status?.[j.status]||j.status;
                    const statusBg=j.status==="inProgress"?"#dbeafe":"#fef9c3";
                    const statusFg=j.status==="inProgress"?"#1d4ed8":"#854d0e";
                    return (
                      <div key={j.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:i<activeJobsToday.length-1?"1px solid var(--border)":"none"}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontWeight:600,fontSize:13,color:"var(--text)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{j.title}</div>
                          <div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>{custName}</div>
                        </div>
                        {(j.crewNames||[]).length>0&&(
                          <div style={{display:"flex",gap:4,flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end",maxWidth:200}}>
                            {(j.crewNames||[]).slice(0,3).map(name=>{
                              const color=getCrewColor(name,teamMembers,jobs,[]);
                              return (
                                <span key={name} title={name} style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:11,color:"var(--text-2)",background:color+"1a",borderRadius:12,padding:"2px 7px",whiteSpace:"nowrap"}}>
                                  <span style={{width:6,height:6,borderRadius:"50%",background:color,flexShrink:0,display:"inline-block"}}/>
                                  {name}
                                </span>
                              );
                            })}
                            {(j.crewNames||[]).length>3&&<span style={{fontSize:11,color:"var(--text-3)"}}>+{j.crewNames.length-3}</span>}
                          </div>
                        )}
                        <span style={{background:statusBg,color:statusFg,borderRadius:4,padding:"2px 8px",fontSize:11,fontWeight:600,whiteSpace:"nowrap",flexShrink:0}}>{statusLabel}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* On-site employees */}
              {onSiteTimesheets.length>0&&(
                <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:10,overflow:"hidden"}}>
                  <div style={{padding:"8px 14px",borderBottom:"1px solid var(--border)",fontSize:11,fontWeight:700,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:".07em",background:"var(--bg-2)"}}>
                    {lang==='fr'?'Employés sur site':'On Site'} <span style={{fontWeight:400,opacity:.6}}>({onSiteTimesheets.length})</span>
                  </div>
                  {onSiteTimesheets.map((ts,i)=>(
                    <div key={ts.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:i<onSiteTimesheets.length-1?"1px solid var(--border)":"none"}}>
                      <span style={{width:8,height:8,borderRadius:"50%",background:"#16a34a",flexShrink:0,display:"inline-block",boxShadow:"0 0 0 3px #dcfce7"}}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600,fontSize:13,color:"var(--text)"}}>{ts.employeeName||"—"}</div>
                        <div style={{fontSize:11,color:"var(--text-3)",marginTop:2}}>{jobTitleById(ts.jobId)}</div>
                      </div>
                      <span style={{fontSize:12,color:"var(--text-3)",flexShrink:0,whiteSpace:"nowrap"}}>
                        {lang==='fr'?'Entrée':'In'} {fmtTime(ts.punchInFinal||ts.punchIn)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Payments due today */}
              {paymentsToday.length>0&&(
                <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:10,overflow:"hidden"}}>
                  <div style={{padding:"8px 14px",borderBottom:"1px solid #fcd34d",fontSize:11,fontWeight:700,color:"#854d0e",textTransform:"uppercase",letterSpacing:".07em",background:"#fef9c3"}}>
                    {lang==='fr'?'Paiements dus aujourd\'hui':'Payments Due Today'} <span style={{fontWeight:400,opacity:.7}}>({paymentsToday.length})</span>
                  </div>
                  {paymentsToday.map(({inv,ms},i)=>(
                    <div key={`${inv.id}-${ms.id}`} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",borderBottom:i<paymentsToday.length-1?"1px solid #fef3c7":"none"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600,fontSize:13,color:"var(--text)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{cName(inv.customerId)}</div>
                        <div style={{fontSize:11,color:"#92400e",marginTop:2}}>{inv.number?`#${inv.number} — `:""}{ms.label}</div>
                      </div>
                      <span style={{fontSize:13,fontWeight:700,color:"#d97706",flexShrink:0,whiteSpace:"nowrap"}}>{fmt(ms.amount||0)}</span>
                    </div>
                  ))}
                </div>
              )}

            </div>
          )}
        </div>

        {/* ── Stats Cards ── */}
        <div className="stat-grid">
          {[
            {label:t.dashboard.totalCustomers,value:customers.length,sub:`${customers.filter(c=>c.status==="Prospect").length} ${t.dashboard.active}`,icon:"customers",iconBg:"#eff6ff",iconColor:"#2563eb"},
            {label:t.dashboard.activeLeads,value:activeLeads.length,sub:`${wonLeads.length} ${t.dashboard.wonPeriod}`,icon:"leads",iconBg:"#f0f9ff",iconColor:"#0891b2"},
            {label:t.dashboard.pipelineValue,value:fmt(pipelineVal),sub:`${estimates.length} ${t.dashboard.pendingEst}`,icon:"dollar",iconBg:"#fefce8",iconColor:"#ca8a04"},
            {label:t.dashboard.activeJobs,value:activeJobs.length,sub:`${jobs.filter(j=>j.status==="completed").length} ${t.dashboard.approved}`,icon:"jobs",iconBg:"#f0fdf4",iconColor:"#16a34a"},
          ].map((s,i)=>(
            <div key={i} className="stat-card">
              <div className="stat-row">
                <div><div className="stat-label">{s.label}</div><div className="stat-value" style={{fontSize:typeof s.value==="string"&&s.value.length>8?22:32}}>{s.value}</div><div className="stat-sub">{s.sub}</div></div>
                <div className="stat-icon" style={{background:s.iconBg,color:s.iconColor}}><Icon name={s.icon} size={20}/></div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Suivi financier ── */}
        <div style={{marginTop:20,marginBottom:20}}>
          <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:15,marginBottom:12,color:"var(--text)"}}>{t.dashboard.suiviFinancier}</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:14}}>
            {[
              {label:t.dashboard.aEnvoyer,   value:fmt(aEnvoyerTotal),   sub:`${aEnvoyerCount} soumission${aEnvoyerCount!==1?"s":""}`, color:"#d97706", page:"estimates"},
              {label:t.dashboard.enAttente,   value:fmt(enAttenteTotal),  sub:`${enAttenteInvIds.size} facture${enAttenteInvIds.size!==1?"s":""}`, color:"#2563eb", page:"invoices"},
              {label:t.dashboard.enRetard,    value:fmt(enRetardTotal),   sub:`${enRetardInvIds.size} facture${enRetardInvIds.size!==1?"s":""}`, color:"var(--red)", page:"invoices"},
              {label:t.dashboard.encaisse,    value:fmt(encaisseTotal),   sub:t.dashboard.cetteAnnee, color:"#16a34a", page:"invoices"},
            ].map((card,i)=>(
              <div key={i} onClick={()=>setPage(card.page)} style={{cursor:"pointer",background:"var(--surface)",border:"1px solid var(--border)",borderRadius:10,padding:"14px 16px",borderLeft:`4px solid ${card.color}`}}>
                <div style={{fontSize:11,fontWeight:600,color:card.color,textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>{card.label}</div>
                <div style={{fontSize:22,fontWeight:700,color:"var(--text)",lineHeight:1.1}}>{card.value}</div>
                <div style={{fontSize:11,color:"var(--text-3)",marginTop:4}}>{card.sub}</div>
              </div>
            ))}
          </div>
          <div style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:10,overflow:"hidden"}}>
            <button type="button" onClick={()=>setSuiviExpanded(p=>!p)} style={{width:"100%",padding:"11px 16px",background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",fontFamily:"var(--font-display)",fontWeight:600,fontSize:13,color:"var(--text)"}}>
              <span>{suiviExpanded?t.dashboard.cacherSuivi:t.dashboard.afficherSuivi}</span>
              <span style={{fontSize:11,color:"var(--text-3)"}}>{suiviExpanded?"▴":"▾"}</span>
            </button>
            {suiviExpanded&&(
              <div style={{borderTop:"1px solid var(--border)",overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                  <thead>
                    <tr style={{background:"var(--bg-2)"}}>
                      {[{key:"projet",label:"Projet"},{key:"client",label:"Client"},{key:"soumission",label:"Soumission"},{key:"facture",label:"Facture"},{key:"enRetard",label:"En retard"},{key:"dernierEnvoi",label:"Dernier envoi"},{key:"action",label:""}].map(col=>(
                        <th key={col.key} onClick={col.key!=="action"&&col.key!=="soumission"&&col.key!=="facture"&&col.key!=="dernierEnvoi"?()=>setSuiviSort(s=>({col:col.key,dir:s.col===col.key&&s.dir==="asc"?"desc":"asc"})):undefined}
                          style={{padding:"7px 12px",textAlign:"left",fontWeight:600,fontSize:11,color:"var(--text-3)",borderBottom:"1px solid var(--border)",whiteSpace:"nowrap",userSelect:"none",cursor:col.key!=="action"&&col.key!=="soumission"&&col.key!=="facture"&&col.key!=="dernierEnvoi"?"pointer":"default"}}>
                          {col.label}{suiviSort.col===col.key?` ${suiviSort.dir==="asc"?"↑":"↓"}`:""}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {suiviRows.map((row,i)=>(
                      <tr key={row.job.id} style={{borderBottom:"1px solid var(--border)",background:i%2===0?"var(--surface)":"var(--bg-2)"}}>
                        <td style={{padding:"8px 12px",fontWeight:600,whiteSpace:"nowrap"}}>{(row.job.jobTypeTag==="warranty"||row.job.isWarranty)&&<span style={{marginRight:3}}>🔧</span>}{row.job.title}</td>
                        <td style={{padding:"8px 12px",color:"var(--text-2)",whiteSpace:"nowrap"}}>{row.clientName}</td>
                        <td style={{padding:"8px 12px",whiteSpace:"nowrap"}}>{row.estCell}</td>
                        <td style={{padding:"8px 12px",whiteSpace:"nowrap"}}>{row.invCell}</td>
                        <td style={{padding:"8px 12px",whiteSpace:"nowrap",fontWeight:row.overdueAmt>0?700:400,color:row.overdueAmt>0?"var(--red)":"var(--text-3)"}}>{row.overdueAmt>0?fmt(row.overdueAmt):"—"}</td>
                        <td style={{padding:"8px 12px",color:"var(--text-3)",whiteSpace:"nowrap",fontSize:11}}>{row.lastSent}</td>
                        <td style={{padding:"8px 12px"}}>
                          {row.actionBtn?<button type="button" className="btn btn-secondary btn-sm" onClick={()=>setPage(row.actionPage)} style={{fontSize:11,whiteSpace:"nowrap",...(row.actionColor?{color:row.actionColor,borderColor:row.actionColor}:{})}}>{row.actionBtn}</button>:<span style={{color:"var(--text-3)"}}>—</span>}
                        </td>
                      </tr>
                    ))}
                    {suiviRows.length===0&&(
                      <tr><td colSpan={7} style={{padding:"20px",textAlign:"center",color:"var(--text-3)",fontSize:12}}>Aucun projet à suivre</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ── Activity Feed ── */}
        <div style={{marginTop:24,marginBottom:24}}>
          <div style={{display:"flex",alignItems:"center",marginBottom:12}}>
            <div style={{fontFamily:"var(--font-display)",fontWeight:700,fontSize:15,color:"var(--text)",flex:1}}>{t.dashboard.attentionFeed}</div>
            {hasDismissed&&<button type="button" onClick={clearAllDismissed} style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:"var(--text-3)",padding:"2px 0",fontFamily:"inherit"}}>{lang==="fr"?"Tout effacer":"Clear all"}</button>}
          </div>
          {feedItems.length===0 ? (
            <div style={{padding:"16px 20px",background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:10,color:"#16a34a",fontWeight:600,fontSize:14,display:"flex",alignItems:"center",gap:8}}>
              <span>✓</span> {t.dashboard.allUpToDate}
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {feedItems.map(item=>{
                const isMsg = item.type==="message";
                const isExpanded = expandedMsg===item.data?.id;
                const isTs = item.type==="timesheetsPending"||item.type==="timesheetsRejected";
                const ty = item.type==="thankYou" ? (tyState[item.data?.id]||{}) : null;
                const handleClick = () => {
                  if (isMsg) { setExpandedMsg(isExpanded?null:item.data.id); return; }
                  if (item.type==="draftEst") { onNavigateToEstimate?.(item.data.id); return; }
                  if (item.type==="completedNoInvoice") { setPage("invoices"); return; }
                  if (item.type==="thankYou") return;
                  if (item.type==="invoice"||item.type==="milestone"||item.type==="pendingCO"||item.type==="upcomingDeposit") setPage("invoices");
                  else if (item.type==="estimate"||item.type==="approved") setPage("estimates");
                  else if (isTs) setPage("timesheets");
                };
                return (
                  <div key={item.key} style={{background:"var(--surface)",border:"1px solid var(--border)",borderRadius:10,overflow:"hidden",position:"relative"}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 16px",cursor:item.type==="thankYou"?"default":"pointer",userSelect:"none"}} onClick={handleClick}>
                      <span style={{fontSize:18,flexShrink:0,lineHeight:1}}>{item.emoji}</span>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600,fontSize:13,color:"var(--text)"}}>{item.title}</div>
                        {item.bold&&<div style={{fontSize:12,color:"#78716c",marginTop:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{item.bold}</div>}
                        {item.sub&&<div style={{fontSize:12,color:"#a8a29e",marginTop:1}}>{item.sub}</div>}
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                        {item.timeVal&&<span style={{fontSize:11,color:"#a8a29e"}}>{timeAgo(item.timeVal)}</span>}
                        {/* Inline action buttons for new feed types */}
                        {item.type==="draftEst"&&(
                          <button type="button" className="btn btn-secondary btn-sm" style={{fontSize:11,whiteSpace:"nowrap"}} onClick={e=>{e.stopPropagation();onNavigateToEstimate?.(item.data.id);}}>
                            {lang==="fr"?"Ouvrir":"Open"}
                          </button>
                        )}
                        {item.type==="completedNoInvoice"&&(
                          <button type="button" className="btn btn-secondary btn-sm" style={{fontSize:11,whiteSpace:"nowrap"}} onClick={e=>{e.stopPropagation();setPage("invoices");}}>
                            {lang==="fr"?"Facturer":"Create Invoice"}
                          </button>
                        )}
                        {item.type==="thankYou"&&(
                          ty?.sent ? (
                            <span style={{fontSize:12,color:"#16a34a",fontWeight:600,whiteSpace:"nowrap"}}>✓ {lang==="fr"?"Envoyé":"Sent"}</span>
                          ) : ty?.error ? (
                            <span style={{fontSize:12,color:"var(--red)",whiteSpace:"nowrap",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis"}}>{ty.error}</span>
                          ) : (
                            <button type="button" className="btn btn-secondary btn-sm" style={{fontSize:11,whiteSpace:"nowrap"}} disabled={ty?.sending} onClick={async e=>{
                              e.stopPropagation();
                              const inv = item.data;
                              const customer = (customers||[]).find(c=>c.id===inv.customerId);
                              if (!customer?.email||!sendThankYouEmail) return;
                              setTyState(p=>({...p,[inv.id]:{sending:true}}));
                              const result = await sendThankYouEmail(customer, inv, settings, lang);
                              if (result.success) {
                                setTyState(p=>({...p,[inv.id]:{sent:true}}));
                                dismissItem(item.key, "local");
                              } else {
                                setTyState(p=>({...p,[inv.id]:{error:result.error||"Erreur"}}));
                              }
                            }}>
                              {ty?.sending?"…":(lang==="fr"?"Envoyer":"Send")}
                            </button>
                          )
                        )}
                        {isMsg&&<Icon name={isExpanded?"chevronDown":"chevronRight"} size={14}/>}
                        <button
                          type="button"
                          title={lang==="fr"?"Masquer":"Dismiss"}
                          onClick={e=>{ e.stopPropagation(); dismissItem(item.key, item.storage); }}
                          style={{background:"none",border:"none",cursor:"pointer",padding:2,display:"flex",alignItems:"center",color:"var(--text-3)",opacity:0.4,transition:"opacity .15s",flexShrink:0}}
                          onMouseEnter={e=>e.currentTarget.style.opacity=1}
                          onMouseLeave={e=>e.currentTarget.style.opacity=0.4}
                        ><Icon name="x" size={14}/></button>
                      </div>
                    </div>
                    {isMsg&&isExpanded&&(
                      <div style={{borderTop:"1px solid var(--border)",padding:"12px 16px",background:"#fafaf9"}}>
                        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:12,maxHeight:200,overflowY:"auto"}}>
                          {(comms||[]).filter(c=>c.type==="portal"&&c.customerId===item.data.customerId).sort((a,b)=>{const da=toDate(a.createdAt),db=toDate(b.createdAt);return (da||0)-(db||0);}).map(c=>(
                            <div key={c.id} style={{display:"flex",flexDirection:"column",alignSelf:c.direction==="inbound"?"flex-start":"flex-end",maxWidth:"80%",gap:2}}>
                              <div style={{padding:"8px 10px",borderRadius:8,fontSize:12,background:c.direction==="inbound"?"#eff6ff":"#f0fdf4",border:c.direction==="inbound"?"1px solid #bfdbfe":"1px solid #bbf7d0",color:"#44403c"}}>{c.body||c.subject}</div>
                              <div style={{fontSize:10,color:"#a8a29e",textAlign:c.direction==="inbound"?"left":"right"}}>{c.direction==="inbound"?cName(c.customerId):t.dashboard.us} · {timeAgo(c.createdAt)}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
                          <textarea className="form-input" style={{flex:1,minHeight:56,resize:"vertical",fontSize:12}} placeholder={`${t.dashboard.reply}…`} value={replyTexts[item.data.id]||""} onChange={e=>setReplyTexts(p=>({...p,[item.data.id]:e.target.value}))} onKeyDown={e=>{if(e.key==="Enter"&&(e.metaKey||e.ctrlKey))sendReply(item.data);}}/>
                          <button className="btn btn-primary" style={{whiteSpace:"nowrap",flexShrink:0}} disabled={sendingReply||!(replyTexts[item.data.id]||"").trim()} onClick={()=>sendReply(item.data)}>{t.dashboard.send}</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Pipeline + Recent Activity ── */}
        <div className="grid-2">
          <div className="card">
            <div style={{padding:"16px 20px 0",fontFamily:"var(--font-display)",fontWeight:600,fontSize:14}}>{t.dashboard.salesPipeline}</div>
            <div className="pipeline-chart">
              <div className="bar-chart">
                {stageData.map((s,i)=>(
                  <div key={i} className="bar-group">
                    <div className="bar" style={{height:`${(s.count/maxCount)*100}px`,background:STAGE_COLORS[s.stage]}}/>
                    <div className="bar-label">{t.leads.stages[s.stage]}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="card">
            <div style={{padding:"16px 20px 12px",fontFamily:"var(--font-display)",fontWeight:600,fontSize:14}}>{t.dashboard.recentActivity}</div>
            <div style={{padding:"0 20px 16px"}}>
              {(recent.length>0?recent:[{type:"estimate",text:"No recent activity",time:""}]).map((a,i)=>(
                <div key={i} className="activity-item">
                  <div className="activity-icon" style={{background:a.type==="estimate"?"#fef3c7":"#eff6ff",color:a.type==="estimate"?"#d97706":"#2563eb"}}>
                    <Icon name={a.type==="estimate"?"estimates":"customers"} size={13}/>
                  </div>
                  <div><div className="activity-text">{a.text}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default Dashboard;
